// src/components/Common/Header.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { useUser } from '../../context/UserContext';
import { productService } from '../../services/productService';
import { addressService } from '../../services/addressService';
import ProductCard from '../Products/ProductCard';
import AddressModal from '../Address/AddressModal';

// IMPORTED MODAL
const getIconForCategory = (name) => {
  if (!name) return 'fa-box';
  const lowerName = name.toLowerCase();
  if (lowerName.includes('groc')) return 'fa-shopping-basket';
  if (lowerName.includes('fresh') || lowerName.includes('veg') || lowerName.includes('fruit')) return 'fa-leaf';
  if (lowerName.includes('dair') || lowerName.includes('milk')) return 'fa-tint';
  if (lowerName.includes('snack') || lowerName.includes('biscuit') || lowerName.includes('chip')) return 'fa-cookie';
  if (lowerName.includes('drink') || lowerName.includes('bever') || lowerName.includes('tea')) return 'fa-coffee';
  if (lowerName.includes('elect') || lowerName.includes('mobil') || lowerName.includes('lap') || lowerName.includes('tech')) return 'fa-mobile-alt';
  if (lowerName.includes('fash') || lowerName.includes('cloth') || lowerName.includes('wear')) return 'fa-tshirt';
  if (lowerName.includes('home') || lowerName.includes('kitch')) return 'fa-home';
  if (lowerName.includes('coup') || lowerName.includes('offer')) return 'fa-tags';
  return 'fa-box';
}

const Header = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { getCartCount, addToCart } = useCart();
  const { getWishlistCount } = useWishlist();
  const { user, isLoggedIn } = useUser();
  const cartCount = getCartCount();
  const wishlistCount = getWishlistCount();

  const [activeMode, setActiveMode] = useState('quick');
  const [activeCategory, setActiveCategory] = useState('low-price');
  const [backendCategories, setBackendCategories] = useState([]);

  // Location Modal States
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [userLocation, setUserLocation] = useState({
    city: '', state: '', pincode: '', address: 'Select Location', lat: null, lng: null
  });

  // Saved Addresses State
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

  // Search Modal States
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState({ categories: [], outletsVariants: [], products: [] });
  const [isSearching, setIsSearching] = useState(false);

  const desktopLocationRef = useRef(null);
  const mobileLocationRef = useRef(null);
  const desktopSearchRef = useRef(null);
  const mobileSearchRef = useRef(null);

  useEffect(() => {
    const fetchHeaderCategories = async () => {
      try {
        const deliveryType = activeMode === 'quick' ? 'QUICK' : 'STANDARD';
        const data = await productService.getCategories(deliveryType);
        setBackendCategories(data || []);
      } catch (err) {
        console.error("Failed to fetch header categories:", err);
        setBackendCategories([]);
      }
    };
    fetchHeaderCategories();
  }, [activeMode]);

  useEffect(() => {
    const params = new URLSearchParams(routerLocation.search);
    const modeParam = params.get('mode');
    
    if (modeParam) {
      setActiveMode(modeParam.toLowerCase());
    } else if (routerLocation.pathname.includes('standard')) {
      setActiveMode('standard');
    } else if (routerLocation.pathname.includes('quick')) {
      setActiveMode('quick');
    }
    const cat = params.get('category');
    if (cat) {
      setActiveCategory(cat);
    } else if (!routerLocation.pathname.includes('search') && !routerLocation.pathname.includes('offers')) {
      setActiveCategory(routerLocation.pathname.includes('standard') ? 'all' : 'low-price');
    }

    // FIXED: Close the search dropdown on any route change (e.g. clicking a product card)
    setShowSearchDropdown(false);
  }, [routerLocation]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (desktopSearchRef.current && !desktopSearchRef.current.contains(event.target)) &&
        (mobileSearchRef.current && !mobileSearchRef.current.contains(event.target))
      ) {
        setShowSearchDropdown(false);
      }
      
      // Prevent closing location modal if clicking inside AddressModal (which portals or sits on top)
      if (document.getElementById('address-form')?.contains(event.target)) {
        return;
      }

      if (
        (desktopLocationRef.current && !desktopLocationRef.current.contains(event.target)) &&
        (mobileLocationRef.current && !mobileLocationRef.current.contains(event.target))
      ) {
        setShowLocationModal(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (searchQuery.trim().length > 0) {
        setIsSearching(true);
        try {
          const data = await productService.searchProducts(searchQuery.trim());
          setSearchResults(data || { categories: [], outletsVariants: [], products: [] });
        } catch (e) {
          console.error(e);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults({ categories: [], outletsVariants: [], products: [] });
      }
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Fetch saved addresses if logged in and location modal is opened
  useEffect(() => {
    if (isLoggedIn() && showLocationModal) {
      fetchSavedAddresses();
    }
  }, [isLoggedIn, showLocationModal]);

  const fetchSavedAddresses = async () => {
    try {
      const data = await addressService.getAll();
      setSavedAddresses(data || []);
    } catch (e) {
      console.log("Could not load addresses for modal");
    }
  };

  const fetchCurrentLocation = (isSilent = false) => {
    if (!navigator.geolocation) {
      if (!isSilent) alert('Geolocation is not supported by your browser');
      return;
    }

    setIsFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const res = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
          const data = await res.json();
          if (data && data.address) {
            const place = data.address;
            const city = place.city || place.town || place.village || place.county || '';
            const state = place.state || '';
            const pincode = place.postcode || '';
            
            const newLoc = {
              city: city || 'Location Found',
              state,
              pincode,
              address: `${city}, ${state} ${pincode}`.replace(/^, /, '').trim() || 'Unknown Address',
              lat: latitude,
              lng: longitude
            };
            setUserLocation(newLoc);
            localStorage.setItem('svmart_location', JSON.stringify(newLoc));
            setShowLocationModal(false);
          }
        } catch (error) {
          if (!isSilent) alert('Could not fetch address details automatically.');
        } finally {
          setIsFetchingLocation(false);
        }
      },
      (error) => {
        setIsFetchingLocation(false);
        if (!isSilent) alert('Please allow location access in your browser to view the map.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
    );
  };

  useEffect(() => {
    const saved = localStorage.getItem('svmart_location');
    if (saved) {
      try { setUserLocation(JSON.parse(saved)); } catch (e) {}
    } else {
      fetchCurrentLocation(true);
    }
  }, []);

  const handleModeChange = (mode) => {
    setActiveMode(mode);
    setShowSearchDropdown(false);
    
    if (routerLocation.pathname.includes('/offers')) {
      navigate(`/offers?mode=${mode}`);
    } else if (routerLocation.pathname.includes('/search')) {
      const params = new URLSearchParams(routerLocation.search);
      const q = params.get('query') || '';
      navigate(`/search?query=${encodeURIComponent(q)}&mode=${mode}`);
    } else if (!['/cart', '/wishlist', '/checkout', '/profile', '/addresses'].some(path => routerLocation.pathname.includes(path))) {
      navigate(`/${mode}?category=${mode === 'quick' ? 'low-price' : 'all'}`);
    }
  };

  const handleCategoryClick = (categoryName, categoryId) => {
    const catSlug = categoryName.toLowerCase().replace(/\s+/g, '-');
    setActiveCategory(catSlug);
    navigate(`/${activeMode}?category=${catSlug}&categoryId=${categoryId}`);
    setShowSearchDropdown(false);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?query=${encodeURIComponent(searchQuery.trim())}&mode=${activeMode}`);
      setShowSearchDropdown(false);
    }
  };

  const selectSavedAddress = (addr) => {
    const newLoc = {
      city: addr.city || '',
      state: addr.state || '',
      pincode: addr.pinCode || '',
      address: `${addr.houseNumber}, ${addr.streetName}, ${addr.city}`.replace(/^, /, '').trim(),
      lat: addr.latitude || 0,
      lng: addr.longitude || 0
    };
    setUserLocation(newLoc);
    localStorage.setItem('svmart_location', JSON.stringify(newLoc));
    setShowLocationModal(false);
  };

  // THEME VARIABLES (White background & SV Mart Logo Colors)
  const isQuick = activeMode === 'quick';
  
  // White header background for clean E-Commerce Look
  const headerBg = 'bg-white';
  
  // Highlighting Pills (Yellow for Quick, Green for Standard)
  const pillBg = isQuick ? 'bg-[#FFC20E] text-[#E32726]' : 'bg-[#008A44] text-white'; 
  const inactiveTab = 'text-gray-600 hover:text-gray-900 hover:bg-gray-100';

  // Switcher Styles
  const switcherBg = 'bg-gray-100 border-gray-200';
  const quickBtnActive = isQuick ? 'bg-[#FFC20E] text-[#E32726] shadow-sm' : 'text-gray-500 hover:text-gray-800';
  const stdBtnActive = !isQuick ? 'bg-[#008A44] text-white shadow-sm' : 'text-gray-500 hover:text-gray-800';

  // Text & Icons adapted for White Background
  const textColor = 'text-gray-800';
  const dimTextColor = 'text-gray-500';
  const iconBg = 'bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200';
  const accentColor = isQuick ? 'text-[#E32726]' : 'text-[#008A44]';

  const defaultPills = isQuick 
    ? [{ id: 'low-price', name: 'Low Price', icon: 'fa-check-circle', path: '/quick?category=low-price' }] 
    : [{ id: 'all', name: 'All', icon: 'fa-list', path: '/standard?category=all' }];

  // -----------------------------------------------------
  // Reusable Sub-Components for Modals
  // -----------------------------------------------------
  const LocationPopup = () => (
    <div className="absolute top-full left-0 mt-4 w-[320px] md:w-[380px] bg-white text-gray-900 rounded-3xl shadow-[0_10px_40px_rgba(0,0,0,0.2)] border border-gray-100 p-4 md:p-5 z-[250] cursor-default before:content-[''] before:absolute before:-top-2 before:left-6 before:w-4 before:h-4 before:bg-white before:rotate-45 before:border-l before:border-t before:border-gray-100">
      <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
        <h3 className="text-base font-extrabold text-gray-900">Choose delivery address</h3>
        <button onClick={(e) => { e.stopPropagation(); setShowLocationModal(false); }} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 font-bold transition-colors">
          <i className="fas fa-times text-xs"></i>
        </button>
      </div>

      {isLoggedIn() ? (
        <div className="mb-4 space-y-2 max-h-[200px] overflow-y-auto custom-scrollbar pr-1">
          {savedAddresses.map(addr => (
            <div key={addr.id} onClick={() => selectSavedAddress(addr)} className="p-3 border border-gray-100 rounded-xl hover:bg-sky-50 hover:border-sky-200 cursor-pointer transition-all flex items-start gap-3">
              <i className={`fas mt-1 text-gray-400 ${addr.addressType === 'HOME' ? 'fa-home' : addr.addressType === 'WORK' ? 'fa-briefcase' : 'fa-map-marker-alt'}`}></i>
              <div>
                <p className="text-sm font-bold text-gray-900 line-clamp-1">{addr.addressType} - {addr.receiverName}</p>
                <p className="text-xs text-gray-500 line-clamp-1">{addr.houseNumber}, {addr.streetName}, {addr.city}</p>
              </div>
            </div>
          ))}
          <button onClick={() => { setShowLocationModal(false); setIsAddressModalOpen(true); }} className="w-full p-3 border border-dashed border-gray-200 rounded-xl text-sky-600 font-bold text-sm hover:bg-sky-50 transition-colors flex items-center justify-center gap-2">
            <i className="fas fa-plus"></i> Add New Address
          </button>
        </div>
      ) : (
        <>
          {userLocation.lat && userLocation.lng ? (
            <div className="w-full h-32 md:h-36 rounded-2xl overflow-hidden mb-4 border border-gray-200 shadow-sm pointer-events-none">
              <iframe
                width="100%" height="100%" frameBorder="0" scrolling="no"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${userLocation.lng-0.005}%2C${userLocation.lat-0.005}%2C${userLocation.lng+0.005}%2C${userLocation.lat+0.005}&layer=mapnik&marker=${userLocation.lat}%2C${userLocation.lng}`}
              ></iframe>
            </div>
          ) : (
            <div className="w-full h-24 rounded-2xl bg-gray-50 flex items-center justify-center mb-4 border border-gray-200 text-gray-400">
              <i className="fas fa-map-marker-alt text-3xl mb-2"></i>
              <p className="text-xs font-bold w-full text-center px-4 mt-2">Map Preview Unavailable</p>
            </div>
          )}
        </>
      )}

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); fetchCurrentLocation(false); }}
        disabled={isFetchingLocation}
        className="w-full flex items-center justify-center py-3.5 px-4 rounded-xl border border-sky-100 bg-sky-50/50 hover:bg-sky-50 transition-all text-sky-600 font-bold text-sm gap-2"
      >
        <i className={`fas ${isFetchingLocation ? 'fa-spinner fa-spin' : 'fa-crosshairs'}`}></i>
        {isFetchingLocation ? 'Fetching location...' : 'Use current location'}
      </button>
    </div>
  );

  const SearchDropdown = () => {
    if (!showSearchDropdown) return null;
    
    return (
      <div className="absolute top-full left-0 md:left-auto md:right-0 mt-3 bg-white rounded-2xl shadow-[0_10px_50px_rgba(0,0,0,0.15)] border border-gray-100 p-5 md:p-7 z-[250] text-gray-900 max-h-[85vh] overflow-y-auto w-full md:w-[650px] lg:w-[850px] xl:w-[1000px] max-w-[95vw] cursor-default">
        {isSearching ? (
          <div className="flex flex-col items-center py-10">
            <i className={`fas fa-spinner fa-spin text-4xl ${accentColor} mb-3`}></i>
            <p className="text-gray-500 text-sm font-medium">Fetching best results...</p>
          </div>
        ) : (!searchResults.categories?.length && !searchResults.outletsVariants?.length && searchQuery.length > 0) ? (
          <div className="text-center py-12">
            <i className="fas fa-search text-5xl text-gray-200 mb-4"></i>
            <h3 className="text-xl font-bold text-gray-800">No Results Found</h3>
            <p className="text-sm text-gray-500 mt-1">Try checking your spelling or search another term.</p>
          </div>
        ) : searchQuery.length === 0 ? (
          <div className="text-center py-10 opacity-50">
            <i className="fas fa-keyboard text-4xl text-gray-300 mb-3"></i>
            <p className="text-sm font-bold text-gray-500">Type to explore instant items...</p>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-black text-gray-900">Top Results for "{searchQuery}"</h3>
            </div>
            
            {/* Display Categories in a neat Grid */}
            {searchResults.categories && searchResults.categories.length > 0 && (
              <div className="mb-8">
                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3 border-b border-gray-100 pb-2">Matching Categories</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                  {searchResults.categories.map((cat) => (
                    <button
                      key={cat.categoryId}
                      onClick={() => handleCategoryClick(cat.categoryName, cat.categoryId)}
                      className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50 hover:bg-sky-50 hover:border-sky-200 transition-all text-left group"
                    >
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm group-hover:text-sky-600 transition-colors">
                        <i className={`fas ${getIconForCategory(cat.categoryName)} text-sm text-gray-500 group-hover:text-sky-600`}></i>
                      </div>
                      <span className="text-xs font-bold text-gray-700 line-clamp-2">{cat.categoryName}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Display Products in a Responsive Grid */}
            {searchResults.outletsVariants && searchResults.outletsVariants.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-3 border-b border-gray-100 pb-2">Matching Products</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
                  {searchResults.outletsVariants.map((variant) => (
                    <ProductCard 
                      key={variant.outletVariantId || variant.id || variant.productId} 
                      product={variant} 
                      addToCart={addToCart} 
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <header className={`sticky top-0 z-[100] w-full ${headerBg} shadow-sm border-b border-gray-100 transition-colors duration-300`}>
      
      {/* ============================== */}
      {/* MOBILE VIEW                    */}
      {/* ============================== */}
      <div className="flex flex-col w-full md:hidden px-3 pt-3 pb-2 gap-3">
         
        <div className="flex items-center justify-between w-full">
          <Link className="flex items-center gap-1.5" to="/">
            <div className="flex items-center justify-center">
              <span className="text-[#E32726] text-xl font-black italic tracking-widest drop-shadow-sm">
                SV MART
              </span>
            </div>
          </Link>
          
          <div className={`flex items-center p-1 rounded-lg border ${switcherBg}`}>
            <button onClick={() => handleModeChange('quick')} className={`px-3 py-1.5 rounded-md text-[10px] font-extrabold transition-all ${quickBtnActive}`}>QUICK</button>
            <button onClick={() => handleModeChange('standard')} className={`px-3 py-1.5 rounded-md text-[10px] font-extrabold transition-all ${stdBtnActive}`}>STANDARD</button>
          </div>
        </div>

        <div className="flex items-center justify-between w-full">
          {/* Mobile Location Wrapper with Ref */}
          <div className="relative max-w-[50%]" ref={mobileLocationRef}>
            <div onClick={() => setShowLocationModal(!showLocationModal)} className="flex items-center gap-1.5 cursor-pointer">
              <i className={`fas fa-location-dot text-sm ${accentColor}`}></i>
              <div className="flex flex-col leading-tight">
                <span className={`text-[8px] font-bold ${dimTextColor} uppercase`}>Deliver To</span>
                <span className={`text-[12px] font-bold ${textColor} truncate w-full flex items-center gap-1`}>
                  {userLocation.city || 'Select Location'} <i className={`fas fa-chevron-down text-[8px] ${dimTextColor}`}></i>
                </span>
              </div>
            </div>
            {/* Renders right under the Mobile Location Button */}
            {showLocationModal && <LocationPopup />}
          </div>
          
          <div className="flex items-center gap-4 text-gray-700 flex-shrink-0">
            <button onClick={() => navigate(`/offers?mode=${activeMode}`)} className="relative"><i className="fas fa-tags text-base"></i></button>
            <button onClick={() => navigate('/wishlist')} className="relative">
              <i className="fas fa-heart text-base"></i>
              {wishlistCount > 0 && <span className="absolute -top-1.5 -right-1.5 bg-[#E32726] text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center border border-white">{wishlistCount}</span>}
            </button>
            <Link to="/cart" className="relative">
              <i className="fas fa-shopping-cart text-base"></i>
              {cartCount > 0 && <span className="absolute -top-1.5 -right-1.5 bg-[#E32726] text-white text-[9px] font-black w-4 h-4 rounded-full flex items-center justify-center border border-white">{cartCount}</span>}
            </Link>
            <button onClick={() => isLoggedIn() ? navigate('/profile') : navigate('/login')} className="relative">
              <i className="fas fa-user text-base"></i>
            </button>
          </div>
        </div>

        {/* Mobile Search Wrapper with Ref */}
        <div className="w-full relative" ref={mobileSearchRef}>
          <form onSubmit={handleSearchSubmit} className={`w-full bg-gray-100 text-gray-900 rounded-xl py-2 pl-9 pr-3 text-sm font-medium border border-gray-200 flex items-center`}>
            <i className={`fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400`}></i>
            <input 
              type="text" 
              placeholder={isQuick ? "Search instant items..." : "Search all products..."}
              value={searchQuery}
              onFocus={() => setShowSearchDropdown(true)}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none w-full placeholder-inherit text-inherit"
            />
          </form>
          <SearchDropdown />
        </div>

      </div>

      {/* ============================== */}
      {/* DESKTOP VIEW                   */}
      {/* ============================== */}
      <div className="hidden md:flex items-center justify-between w-full gap-4 max-w-7xl mx-auto px-4 py-3">
        
        <div className="flex items-center gap-4 flex-shrink-0">
          <Link className="flex items-center gap-2.5 flex-shrink-0" to="/">
            <div className="flex items-center justify-center">
              <span className="text-[#E32726] text-3xl font-black italic tracking-widest drop-shadow-sm">
                SV MART
              </span>
            </div>
          </Link>
          
          <div className={`flex items-center p-1 rounded-full border ${switcherBg} flex-shrink-0`}>
            <button onClick={() => handleModeChange('quick')} className={`flex items-center gap-1.5 px-5 py-1.5 rounded-full text-sm font-extrabold transition-all ${quickBtnActive}`}>
              <i className="fas fa-bolt text-xs"></i><span>QUICK</span>
            </button>
            <button onClick={() => handleModeChange('standard')} className={`px-5 py-1.5 rounded-full text-sm font-extrabold transition-all ${stdBtnActive}`}>
              STANDARD
            </button>
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 flex-1 max-w-3xl">
          
          {/* Desktop Location Wrapper with Ref */}
          <div className="relative flex-shrink-0" ref={desktopLocationRef}>
            <div onClick={() => setShowLocationModal(!showLocationModal)} className="flex items-center gap-2.5 cursor-pointer hover:bg-gray-50 px-3 py-1.5 rounded-xl transition-all border border-transparent hover:border-gray-200">
              <i className={`fas fa-location-dot text-base ${accentColor}`}></i>
              <div className="flex flex-col">
                <div className={`flex items-center gap-1 text-xs font-bold ${dimTextColor} uppercase tracking-wider leading-none`}>
                  <span>Deliver To</span><i className={`fas fa-chevron-down text-[9px] ${dimTextColor} mt-0.5`}></i>
                </div>
                <span className={`text-xs font-extrabold ${textColor} max-w-[130px] truncate mt-1`}>
                  {userLocation.address}
                </span>
              </div>
            </div>
            {/* Renders right under the Desktop Location Button */}
            {showLocationModal && <LocationPopup />}
          </div>
          
          {/* Desktop Search Wrapper with Ref */}
          <div className="flex-1 w-full relative cursor-text" ref={desktopSearchRef}>
            <form onSubmit={handleSearchSubmit} className={`w-full bg-gray-100 text-gray-900 rounded-xl py-2.5 pl-11 pr-4 text-sm font-medium border border-gray-200 flex items-center`}>
              <i className={`fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-400`}></i>
              <input 
                type="text" 
                placeholder={isQuick ? "Search instant items..." : "Search all products..."}
                value={searchQuery}
                onFocus={() => setShowSearchDropdown(true)}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-transparent border-none outline-none w-full placeholder-inherit text-inherit"
              />
            </form>
            <SearchDropdown />
          </div>

        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <button onClick={() => navigate(`/offers?mode=${activeMode}`)} className={`w-10 h-10 rounded-full ${iconBg} flex items-center justify-center relative transition-all shadow-sm`}>
            <i className="fas fa-tags text-sm"></i>
          </button>
          <button onClick={() => navigate('/wishlist')} className={`w-10 h-10 rounded-full ${iconBg} flex items-center justify-center relative transition-all shadow-sm`}>
            <i className="fas fa-heart text-sm"></i>
            {wishlistCount > 0 && <span className="absolute -top-1 -right-1 bg-[#E32726] text-white text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">{wishlistCount}</span>}
          </button>
          <Link to="/cart" className={`w-10 h-10 rounded-full ${iconBg} flex items-center justify-center relative transition-all shadow-sm`}>
            <i className="fas fa-shopping-cart text-sm"></i>
            {cartCount > 0 && <span className="absolute -top-1 -right-1 bg-[#E32726] text-white text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">{cartCount}</span>}
          </Link>
          <button onClick={() => isLoggedIn() ? navigate('/profile') : navigate('/login')} className={`w-10 h-10 rounded-full ${iconBg} flex items-center justify-center transition-all shadow-sm`}>
            <i className="fas fa-user text-sm"></i>
          </button>
        </div>

      </div>

      {/* BOTTOM PILLS BAR */}
      <div className="border-t border-gray-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center gap-3 overflow-x-auto hide-scrollbar">
          <style>{`.hide-scrollbar::-webkit-scrollbar { display: none !important; } .hide-scrollbar { -ms-overflow-style: none !important; scrollbar-width: none !important; }`}</style>
          
          {defaultPills.map(pill => {
            const isActive = activeCategory === pill.id;
            return (
              <button key={pill.id} onClick={() => { setActiveCategory(pill.id); navigate(pill.path); setShowSearchDropdown(false); }} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all flex-shrink-0 ${isActive ? `${pillBg} shadow-md` : inactiveTab}`}>
                <i className={`fas ${pill.icon} text-sm`}></i><span>{pill.name}</span>
              </button>
            );
          })}
          
          {backendCategories.map((cat) => {
            const catSlug = cat.categoryName.toLowerCase().replace(/\s+/g, '-');
            const isActive = activeCategory === catSlug;
            return (
              <button key={cat.categoryId} onClick={() => handleCategoryClick(cat.categoryName, cat.categoryId)} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all flex-shrink-0 ${isActive ? `${pillBg} shadow-md` : inactiveTab}`}>
                <i className={`fas ${getIconForCategory(cat.categoryName)} text-sm`}></i><span>{cat.categoryName}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Reusable Address Modal attached directly to Header so it opens smoothly across pages */}
      <AddressModal 
        isOpen={isAddressModalOpen} 
        onClose={() => setIsAddressModalOpen(false)} 
        onSuccess={() => {
          if(showLocationModal) fetchSavedAddresses();
        }} 
      />
    </header>
  );
};

export default Header;